// src/hooks/useMasterData.js
import { useState, useCallback } from "react";
import { api_path } from "../shared.js";

const BASE        = `${api_path}/master/master_data.php`;
const AUTH_TOKEN  = process.env.REACT_APP_API_TOKEN || "";

const reqHeaders = {
  "Content-Type":  "application/json",
  "Authorization": AUTH_TOKEN ? `Bearer ${AUTH_TOKEN}` : "",
};

async function apiFetch(table, method = "GET", id = null, body = null) {
  const params = new URLSearchParams({ table });
  if (id !== null) params.set("id", id);
  const url = `${BASE}?${params}`;
  const res  = await fetch(url, {
    method,
    credentials: "include",
    headers:     reqHeaders,
    body:        body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.message ?? "API error");
  return json;
}

export function useMasterData(tableKey) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const clearError = () => setError(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(tableKey, "GET");
      setRows(res.data ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tableKey]);

  const create = useCallback(async (payload) => {
    const res     = await apiFetch(tableKey, "POST", null, payload);
    const created = res.data;
    setRows(prev => [...prev, created]);
    return created;
  }, [tableKey]);

  const update = useCallback(async (id, payload) => {
    const res     = await apiFetch(tableKey, "PUT", id, payload);
    const updated = res.data;
    setRows(prev => prev.map(r => r.id === id ? updated : r));
    return updated;
  }, [tableKey]);

  const remove = useCallback(async (id) => {
    await apiFetch(tableKey, "DELETE", id);
    setRows(prev => prev.filter(r => r.id !== id));
  }, [tableKey]);

  return { rows, loading, error, clearError, fetchAll, create, update, remove };
}
